// Pure timestamp parsing and display formatting.
//
// Every display formatter takes an explicit IANA `timeZone`. Components must
// obtain it from the global display setting (`useDisplayTimeZone` in
// `./displayTimeZone`) rather than choosing a zone themselves.

export type TimestampInput = string | number | Date | null | undefined;

export const UTC_TIME_ZONE = "UTC";

const DATE_TIME_WITHOUT_ZONE_RE = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/;
const EXPLICIT_ZONE_RE = /(z|[+-]\d{2}:?\d{2})$/i;
const DISPLAY_LOCALE = "en-US";

// English locales whose CLDR data carries short zone names (EST, IST, AEST,
// SGT, ...). Only English locales are consulted so labels match the UI text.
const ABBREVIATION_FALLBACK_LOCALES = [
  "en-US",
  "en-GB",
  "en-IN",
  "en-AU",
  "en-NZ",
  "en-SG",
  "en-IE",
  "en-CA",
  "en-ZA",
];
const ALPHABETIC_ZONE_NAME_RE = /^[A-Z]{2,5}$/;

/**
 * Browser-resolved IANA zone. Used only to resolve the "Local" display option,
 * never to format a displayed timestamp directly.
 */
export const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || UTC_TIME_ZONE;
  } catch {
    return UTC_TIME_ZONE;
  }
};

const formatterCache = new Map<string, Intl.DateTimeFormat>();

const getFormatter = (
  locale: string,
  options: Intl.DateTimeFormatOptions
) => {
  const cacheKey = `${locale}|${JSON.stringify(options)}`;
  let formatter = formatterCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    formatterCache.set(cacheKey, formatter);
  }
  return formatter;
};

const validTimeZoneCache = new Map<string, string>();

/**
 * Returns `timeZone` when the runtime recognises it, otherwise UTC. Formatting
 * and labelling both go through this so a label can never disagree with the
 * zone the value was actually rendered in.
 */
export const normalizeTimeZone = (timeZone: string | null | undefined) => {
  const candidate = String(timeZone || "").trim();
  if (!candidate) return UTC_TIME_ZONE;

  const cached = validTimeZoneCache.get(candidate);
  if (cached) return cached;

  let normalized = candidate;
  try {
    getFormatter(DISPLAY_LOCALE, { timeZone: candidate });
  } catch {
    normalized = UTC_TIME_ZONE;
  }

  validTimeZoneCache.set(candidate, normalized);
  return normalized;
};

export const parseUtcTimestampToMs = (value: TimestampInput) => {
  if (value == null || value === "") return NaN;

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === "number") {
    return value > 1e12 ? value : value * 1000;
  }

  const rawValue = String(value).trim();
  if (!rawValue) return NaN;

  const normalizedValue =
    DATE_TIME_WITHOUT_ZONE_RE.test(rawValue) && !EXPLICIT_ZONE_RE.test(rawValue)
      ? `${rawValue.replace(" ", "T")}Z`
      : rawValue;

  return new Date(normalizedValue).getTime();
};

/**
 * Minutes east of UTC for `timeZone` at the given instant (IST = 330,
 * EDT = -240). DST-aware because it is derived from the zone's wall clock at
 * that instant.
 */
export const getTimeZoneOffsetMinutes = (
  timeZone: string,
  timestampMs: number = Date.now()
) => {
  const zone = normalizeTimeZone(timeZone);
  if (zone === UTC_TIME_ZONE) return 0;

  const instantMs = Number.isFinite(timestampMs) ? timestampMs : Date.now();
  const wholeSecondMs = Math.floor(instantMs / 1000) * 1000;
  const formatter = getFormatter(DISPLAY_LOCALE, {
    timeZone: zone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
  });
  // formatToParts is ES2017; the project's TS lib target predates it.
  const parts: Array<{ type: string; value: string }> = (
    formatter as any
  ).formatToParts(new Date(wholeSecondMs));
  const getPart = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value);

  const wallClockAsUtcMs = Date.UTC(
    getPart("year"),
    getPart("month") - 1,
    getPart("day"),
    // Some engines render midnight as "24" when hour12 is false.
    getPart("hour") % 24,
    getPart("minute"),
    getPart("second")
  );

  return Math.round((wallClockAsUtcMs - wholeSecondMs) / 60000);
};

/**
 * Highcharts `time` options that place datetime ticks on the display zone's
 * wall clock. Uses our DST-aware offset instead of Highcharts' `timezone`
 * option, whose 11.x offset parser mis-signs negative half-hour zones.
 * Highcharts 11 does not apply `time` changes through `chart.update`, so charts
 * using this must remount (e.g. `key={timeZone}`) when the zone changes.
 */
export const getHighchartsTimeOptions = (timeZone: string) => {
  const zone = normalizeTimeZone(timeZone);

  return {
    useUTC: true,
    // Highcharts expects minutes west of UTC, like Date#getTimezoneOffset.
    getTimezoneOffset: (timestampMs: number) =>
      -getTimeZoneOffsetMinutes(zone, timestampMs),
  };
};

/** "UTC+5:30", "UTC-4", "UTC+0". */
export const formatUtcOffset = (offsetMinutes: number) => {
  const sign = offsetMinutes < 0 ? "-" : "+";
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;

  return `UTC${sign}${hours}${
    minutes ? `:${String(minutes).padStart(2, "0")}` : ""
  }`;
};

// Zones whose abbreviation must not depend on the runtime's ICU locale data.
const FIXED_ZONE_ABBREVIATIONS: Record<string, (offsetMinutes: number) => string> =
  {
    UTC: () => "UTC",
    "Etc/UTC": () => "UTC",
    "America/New_York": (offset) => (offset === -240 ? "EDT" : "EST"),
    "Europe/London": (offset) => (offset === 60 ? "BST" : "GMT"),
    "Asia/Tokyo": () => "JST",
    "Asia/Kolkata": () => "IST",
    "Asia/Calcutta": () => "IST",
  };

const getPreferredEnglishLocales = () => {
  const navigatorLocales =
    typeof navigator !== "undefined"
      ? Array.from(navigator.languages || [navigator.language])
      : [];

  return Array.from(
    new Set([
      ...navigatorLocales.filter((locale) => /^en(?:-|$)/i.test(locale || "")),
      ...ABBREVIATION_FALLBACK_LOCALES,
    ])
  );
};

const abbreviationCache = new Map<string, string | null>();

/**
 * Alphabetic zone abbreviation at an instant ("IST", "EDT", "BST", "JST"), or
 * null when no reliable English abbreviation exists for the zone.
 */
export const getTimeZoneAbbreviation = (
  timeZone: string,
  timestampMs: number = Date.now()
): string | null => {
  const zone = normalizeTimeZone(timeZone);
  const offsetMinutes = getTimeZoneOffsetMinutes(zone, timestampMs);
  const fixedAbbreviation = FIXED_ZONE_ABBREVIATIONS[zone];
  if (fixedAbbreviation) return fixedAbbreviation(offsetMinutes);

  const cacheKey = `${zone}|${offsetMinutes}`;
  if (abbreviationCache.has(cacheKey)) {
    return abbreviationCache.get(cacheKey) ?? null;
  }

  let abbreviation: string | null = null;
  const instant = new Date(
    Number.isFinite(timestampMs) ? timestampMs : Date.now()
  );

  for (const locale of getPreferredEnglishLocales()) {
    try {
      const zoneName = (
        getFormatter(locale, { timeZone: zone, timeZoneName: "short" }) as any
      )
        .formatToParts(instant)
        .find((part: { type: string }) => part.type === "timeZoneName")
        ?.value?.trim();

      // Rejects offset-style names such as "GMT+5:30".
      if (zoneName && ALPHABETIC_ZONE_NAME_RE.test(zoneName)) {
        abbreviation = zoneName;
        break;
      }
    } catch {
      // Unsupported locale in this runtime; try the next one.
    }
  }

  abbreviationCache.set(cacheKey, abbreviation);
  return abbreviation;
};

/**
 * Suffix appended to displayed timestamps: the abbreviation when one exists,
 * otherwise the numeric offset ("UTC+5:45").
 */
export const getTimeZoneSuffix = (
  timeZone: string,
  timestampMs: number = Date.now()
) =>
  getTimeZoneAbbreviation(timeZone, timestampMs) ??
  formatUtcOffset(getTimeZoneOffsetMinutes(timeZone, timestampMs));

const formatInTimeZone = (
  value: TimestampInput,
  options: Intl.DateTimeFormatOptions,
  timeZone: string,
  includeZone = false
) => {
  const timestampMs = parseUtcTimestampToMs(value);
  if (!Number.isFinite(timestampMs)) return "-";

  const zone = normalizeTimeZone(timeZone);
  const formatted = getFormatter(DISPLAY_LOCALE, {
    ...options,
    timeZone: zone,
  }).format(new Date(timestampMs));

  return includeZone
    ? `${formatted} ${getTimeZoneSuffix(zone, timestampMs)}`
    : formatted;
};

/** "02:05:09 PM IST" */
export const formatTimeWithZone = (value: TimestampInput, timeZone: string) =>
  formatInTimeZone(
    value,
    { hour: "2-digit", minute: "2-digit", second: "2-digit" },
    timeZone,
    true
  );

/** "02:05 PM IST" */
export const formatShortTimeWithZone = (
  value: TimestampInput,
  timeZone: string
) =>
  formatInTimeZone(value, { hour: "2-digit", minute: "2-digit" }, timeZone, true);

/** "Jan 19, 2026, 02:05:09 PM IST" */
export const formatDateTimeWithZone = (
  value: TimestampInput,
  timeZone: string
) =>
  formatInTimeZone(
    value,
    {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    },
    timeZone,
    true
  );

/** "Jan 19, 02:05 PM IST" */
export const formatCompactDateTimeWithZone = (
  value: TimestampInput,
  timeZone: string
) =>
  formatInTimeZone(
    value,
    { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" },
    timeZone,
    true
  );

/**
 * "Jan 19 · 02:05 PM IST" for trade header chips. Unparseable input is shown
 * verbatim rather than guessed at.
 */
export const formatExecutionTimestampWithZone = (
  value: string | null | undefined,
  timeZone: string
) => {
  const rawValue = String(value ?? "").trim();
  if (!rawValue) return "\u2014";

  const timestampMs = parseUtcTimestampToMs(rawValue);
  if (!Number.isFinite(timestampMs)) return rawValue;

  return formatInTimeZone(
    timestampMs,
    { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" },
    timeZone,
    true
  ).replace(",", " \u00b7");
};

/** "02:05 PM" — axis labels, where the axis title carries the zone. */
export const formatChartAxisTime = (value: TimestampInput, timeZone: string) =>
  formatInTimeZone(value, { hour: "2-digit", minute: "2-digit" }, timeZone);

const getLightweightChartTimeMs = (time: any) => {
  if (typeof time === "number") return time * 1000;
  if (
    time &&
    typeof time === "object" &&
    Number.isFinite(time.year) &&
    Number.isFinite(time.month) &&
    Number.isFinite(time.day)
  ) {
    return Date.UTC(time.year, time.month - 1, time.day);
  }

  return NaN;
};

// Daily, weekly, and monthly lightweight-chart buckets are aligned to UTC
// midnight, so they represent calendar dates rather than instants. Their
// date-only labels are rendered in UTC; converting them would shift the date
// backwards for zones west of UTC.
const CALENDAR_INTERVALS = ["1d", "1wk", "1mo"];

export const isCalendarChartInterval = (interval: string) =>
  CALENDAR_INTERVALS.includes(interval);

export const formatLightweightChartTime = (
  time: any,
  interval: string,
  timeZone: string
) => {
  const timestampMs = getLightweightChartTimeMs(time);

  if (isCalendarChartInterval(interval)) {
    return formatInTimeZone(
      timestampMs,
      { month: "short", day: "2-digit", year: "2-digit" },
      UTC_TIME_ZONE
    );
  }

  return formatInTimeZone(
    timestampMs,
    { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" },
    timeZone
  );
};

export const formatLightweightChartAxisTime = (
  time: any,
  interval: string,
  tickMarkType: string | undefined,
  timeZone: string
) => {
  const timestampMs = getLightweightChartTimeMs(time);
  if (!Number.isFinite(timestampMs)) return "";

  if (isCalendarChartInterval(interval)) {
    return formatInTimeZone(
      timestampMs,
      {
        month: "short",
        day: "2-digit",
        ...(tickMarkType === "Year" ? { year: "2-digit" as const } : {}),
      },
      UTC_TIME_ZONE
    );
  }

  if (["DayOfMonth", "Month", "Year"].includes(tickMarkType || "")) {
    return formatInTimeZone(
      timestampMs,
      { month: "short", day: "2-digit" },
      timeZone
    );
  }

  return formatInTimeZone(
    timestampMs,
    { hour: "2-digit", minute: "2-digit" },
    timeZone
  );
};
